import { Chart, Plugin, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useScreenSize } from "../../hooks/useScreenSize";
import { PestCount } from "../../types/analysis";
import { formatReferenceNumber } from "../../utils/formatReferenceNumber";

import styles from "./bar-chart.module.css";
import downArrow from "./down_arrow.png";

Chart.register(...registerables);
Chart.register(ChartDataLabels);

const NoPestsPlugin: Plugin = {
  id: "noPestsPlugin",
  afterDatasetsDraw(chart) {
    const {
      ctx,
      chartArea: { top, bottom },
      scales: { x },
    } = chart;
    const labels = chart.data.labels || [];
    const wrapText = (
      ctx: CanvasRenderingContext2D,
      text: string,
      x: number,
      y: number,
      maxWidth: number,
      lineHeight: number
    ) => {
      const words = text.split(" ");
      let line = "";
      const lines: string[] = [];

      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + " ";
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;

        if (testWidth > maxWidth && n > 0) {
          lines.push(line.trim());
          line = words[n] + " ";
        } else {
          line = testLine;
        }
      }
      lines.push(line.trim());

      const totalHeight = lines.length * lineHeight;
      const startY = y - totalHeight / 2 + lineHeight / 2;

      lines.forEach((l, i) => {
        ctx.fillText(l, x, startY + i * lineHeight);
      });
    };
    labels.forEach((label, index) => {
      const monthData = (chart.data as any)[index];

      if (!monthData) return;

      const showNoMessage =
        !monthData.hasAppointments || !monthData.hasInfestation;

      if (!showNoMessage) return;
      const text = !monthData.hasAppointments
        ? "Kein Einsatz vorhanden"
        : "Kein Befall vorhanden";
      const xPos = x.getPixelForValue(index);
      const yPos = (top + bottom) / 2;

      const barWidth =
        (x.getPixelForValue(index + 0.5) - x.getPixelForValue(index - 0.5)) *
        0.9;

      ctx.save();
      ctx.fillStyle = "#3D5170";
      ctx.font = "bold 14px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      wrapText(ctx, text, xPos, yPos, barWidth, 18);

      ctx.restore();
    });
  },
};

Chart.register(NoPestsPlugin);

const createBottomValueLabelsPlugin = (): Plugin => ({
  id: "bottomValueLabels",

  afterDatasetsDraw: (chart: any) => {
    const { ctx, chartArea } = chart;
    const { bottom } = chartArea;

    ctx.save();
    ctx.font = "bold 12px sans-serif";
    ctx.fillStyle = "#3D5170";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      meta.data.forEach((bar: any, index: number) => {
        const value = dataset.data[index];
        if (value == null || isNaN(value) || value <= 0) return;

        const formattedValue = Math.round(Number(value)).toString();

        ctx.fillText(formattedValue, bar.x, bottom + 4);
      });
    });

    ctx.restore();
  },
});
export interface Dataset {
  label: string;
  data: (number | null)[];
  borderWidth: number;
}

interface BarChartProps {
  title: string;
  data: PestCount[];
  noPadding?: boolean;
}

const initialData = {
  labels: [] as string[],
  datasets: [] as Dataset[],
};

const BarChart = ({ title, data, noPadding = false }: BarChartProps) => {
  const [barData, setBarData] = useState(initialData);
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<Chart<"bar"> | null>(null);
  const { t } = useTranslation();
  const { isDesktop } = useScreenSize();

  const hideTimeoutRef = useRef<number | null>(null);
  const [activeBarData, setActiveBarData] = useState<any | null>(null);
  const [expandedMonitors, setExpandedMonitors] = useState<string[]>([]);

  const hasNoData =
    !data || data.length === 0 || data.every((d) => d.pestCounts.length === 0);

  const translateAndFormatMonth = (monthWithYear: string): string => {
    return monthWithYear.replace(/(\w+),(\d+)/, (match, month, year) => {
      const formattedMonth =
        month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
      return `${t(formattedMonth)} ${year}`;
    });
  };

  const toggleMonitor = (id: string) => {
    setExpandedMonitors((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      chartInstance.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const allPests = new Set(
      data.flatMap((entry) => entry.pestCounts.map((pest) => pest.pest))
    );

    const datasets = Array.from(allPests).map((pest: string) => ({
      label: pest.charAt(0).toUpperCase() + pest.slice(1).toLowerCase(),
      data: data.map((entry) => {
        const count =
          entry.pestCounts.find((p) => p.pest === pest)?.count ?? null;
        return count !== null ? Math.round(count) : null;
      }),
      borderWidth: 1,
      minBarLength: 10,
      borderColor: "#fff",
      backgroundColor: " #83D9E1",
      barThickness: isDesktop ? 30 : 35,
      skipNull: true,
      borderRadius: 6,
      datalabels: {
        display: (context: any) => {
          return context.dataset.data[context.dataIndex];
        },
        labels: [
          {
            formatter: (value: any, context: any) => {
              const label = context.dataset?.label || "";

              if (context.dataset.data[context.dataIndex] <= 0) return "";

              if (!isDesktop) {
                const maxLength = 3;

                return label.length > maxLength
                  ? label.substring(0, maxLength) + "..."
                  : label;
              }

              return label;
            },
            align: "end",
            anchor: isDesktop ? "start" : "end",
            rotation: isDesktop ? -90 : 0,
            color: "#3D5170",
            font: {
              size: 10,
              weight: 400,
            },
          },
        ],
      },
    }));

    const widthPerBar = isDesktop ? 30 : 30;
    const sidePaddingPerMonth = 10 * 2;

    const datasetCount = allPests.size;

    const totalWidth =
      data.length * (datasetCount * widthPerBar + sidePaddingPerMonth);

    const calculatedWidth = Math.ceil(totalWidth + (isDesktop ? 200 : 200));

    const chartMinWidth = calculatedWidth;

    setBarData({
      ...data,
      labels: data.map((entry) => translateAndFormatMonth(entry.month)),
      datasets: datasets,
    });

    document.documentElement.style.setProperty(
      "--barchart-min-width",
      `${chartMinWidth}px`
    );
  }, [data, isDesktop]);

  useEffect(() => {
    if (!chartRef.current || !barData.datasets.length) {
      chartInstance?.current?.destroy();
      return;
    }

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const chartCanvas = chartRef.current;

    const chart = new Chart<"bar", (number | null)[], string>(
      chartRef.current,
      {
        type: "bar",
        data: barData as any,
        options: {
          responsive: true,
          onClick: (event, elements) => {
            if (!elements.length) return;

            const { index, datasetIndex } = elements[0];
            const datasetLabel = barData.datasets[datasetIndex]?.label;

            if (!datasetLabel) return;

            const pest = data[index].pestCounts.find(
              (p: any) => p.pest.toLowerCase() === datasetLabel.toLowerCase()
            );

            if (pest) {
              setActiveBarData({
                ...pest,
                month: data[index].month,
              });
              setExpandedMonitors([]);
            }
          },
          onHover: (event, elements) => {
            const target = event.native?.target as HTMLCanvasElement;

            if (!target) return;

            target.style.cursor = elements.length ? "pointer" : "default";
          },
          // skipNull: true,
          layout: {
            padding: {
              top: isDesktop ? 20 : 40,
              right: 10,
              bottom: 35,
              left: 10,
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: false,
            },
          },
          scales: {
            x: {
              position: "top",
              ticks: {
                padding: 10,
              },
            },
            y: {
              ticks: { display: false },
              grid: {
                offset: true,
                display: true,
                color: "#EDEFF5",
                drawTicks: false,
                drawOnChartArea: false,
              },
              beginAtZero: true,
            },
          },
          maintainAspectRatio: false,
        },
        plugins: [NoPestsPlugin, createBottomValueLabelsPlugin()],
      }
    );

    chartInstance.current = chart;

    setTimeout(() => {
      const chartContainer = chartCanvas.parentElement?.parentElement;
      if (chartContainer) {
        chartContainer.scrollLeft = chartContainer.scrollWidth;
      }
    }, 10);

    return () => {
      chartInstance.current?.destroy();

      document.getElementById("service-tooltip")?.remove();
      document.getElementById("custom-tooltip")?.remove();
    };
  }, [barData, isDesktop]);

  const sortedMonitors = [...(activeBarData?.monitors || [])].sort((a, b) => {
    const refA = formatReferenceNumber(a.referenceNumber, a.isTemporary);
    const refB = formatReferenceNumber(b.referenceNumber, b.isTemporary);

    const matchA = refA.match(/^([^\d]+)\s*(\d+)?$/);
    const matchB = refB.match(/^([^\d]+)\s*(\d+)?$/);

    const textA = matchA?.[1]?.trim() ?? "";
    const textB = matchB?.[1]?.trim() ?? "";

    const textCompare = textA.localeCompare(textB);

    if (textCompare !== 0) {
      return textCompare;
    }

    const numA = Number(matchA?.[2] ?? 0);
    const numB = Number(matchB?.[2] ?? 0);

    return numA - numB;
  });

  return (
    <div
      className={styles["barchart"]}
      style={!noPadding && isDesktop ? { padding: "20px" } : { padding: 0 }}
    >
      <p className={styles["barchart__title"]}>{title}</p>

      <div className={styles["barchart__data"]}>
        {hasNoData ? (
          <div className={styles["no-results-container"]}>
            <p className={styles["no-results"]}>{t("NoResults")}</p>
          </div>
        ) : (
          <>
            <div
              style={{
                minWidth: "var(--barchart-min-width)",
                height: isDesktop ? "300px" : "250px",
                position: "relative",
              }}
            >
              <canvas ref={chartRef} />
            </div>
            {activeBarData && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: isDesktop ? "300px" : 0,
                  right: 0,
                  bottom: 0,
                  background: isDesktop
                    ? "rgba(119, 119, 119, 0.3)"
                    : "rgba(174, 174, 174, 0.9)",
                  zIndex: 9999,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: isDesktop ? "center" : "flex-start",
                  paddingTop: isDesktop ? 0 : "95px",
                }}
                onClick={() => {
                  setActiveBarData(null);
                  setExpandedMonitors([]);
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: "12px",
                    width: isDesktop ? "50%" : "90%",
                    maxHeight: "70vh",
                    overflow: "auto",
                    padding: "12px",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ padding: "12px" }}>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: 500,
                        color: "#3D5170",
                      }}
                    >
                      {activeBarData?.pest?.charAt(0).toUpperCase() +
                        activeBarData?.pest?.slice(1)}
                    </div>

                    <div
                      style={{
                        color: "#3D5170",
                        marginTop: "4px",
                        fontSize: "12px",
                      }}
                    >
                      {translateAndFormatMonth(activeBarData?.month)}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        marginTop: "4px",
                        marginBottom: "8px",
                        fontSize: "12px",
                        gap: "2px",
                      }}
                    >
                      <span style={{ color: "#3D5170" }}>Befall: </span>
                      <span style={{ color: "#3D5170" }}>
                        {activeBarData?.monitorsWithCatch} von{" "}
                        {activeBarData?.totalMonitors} Monitoren
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "6px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        color: "#8493AB",
                      }}
                    >
                      <span>Monitor</span>
                      <span>Anzahl</span>
                    </div>

                    {sortedMonitors.map((m: any, idx: number) => (
                      <div key={idx}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "6px 8px",
                            borderTop: "1px solid #F2F2F2",
                            cursor: "pointer",
                            backgroundColor: expandedMonitors.includes(m.id)
                              ? "#E3F9FC"
                              : "transparent",
                            borderRadius: "4px",
                          }}
                          onClick={() => toggleMonitor(m.id)}
                        >
                          <span style={{ color: "#3D5170", fontSize: "14px" }}>
                            {formatReferenceNumber(
                              m.referenceNumber,
                              m.isTemporary
                            )}
                          </span>
                          <span
                            style={{
                              color: expandedMonitors.includes(m.id)
                                ? "#11A8B8"
                                : "#3D5170",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "14px",
                            }}
                          >
                            {m.count}

                            <img
                              src={downArrow}
                              alt="toggle details"
                              style={{
                                transform: expandedMonitors.includes(m.id)
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.2s",
                                display: "block",
                                filter: expandedMonitors.includes(m.id)
                                  ? "invert(48%) sepia(77%) saturate(372%) hue-rotate(140deg) brightness(90%) contrast(90%)"
                                  : "none",
                              }}
                            />
                          </span>
                        </div>

                        {expandedMonitors.includes(m.id) && (
                          <div
                            style={{
                              paddingLeft: "16px",
                              paddingRight: "16px",
                            }}
                          >
                            {Object.entries(
                              m.serviceDetails?.reduce((acc: any, s: any) => {
                                const key =
                                  s.trapType === "Koderfalle"
                                    ? "Köderfalle"
                                    : "Fangfalle";

                                if (!acc[key]) acc[key] = [];
                                acc[key].push(s);
                                return acc;
                              }, {}) || {}
                            ).map(([trapType, services]: any) => (
                              <div
                                key={trapType}
                                style={{ padding: "6px 8px" }}
                              >
                                <div
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    color: "#8493AB",
                                    padding: "4px 0",
                                  }}
                                >
                                  {trapType}
                                </div>

                                {services.map((s: any, i: number) => (
                                  <div
                                    key={i}
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      padding: "6px 0",
                                      color: "#3D5170",
                                      fontSize: "12px",
                                    }}
                                  >
                                    <span>
                                      {s.serviceDate
                                        .split("-")
                                        .reverse()
                                        .join(".")}
                                    </span>
                                    <span style={{ color: "#3D5170" }}>
                                      {s.trapType === "Fangfalle"
                                        ? (s.pestCount ?? 0)
                                        : `${s.eatenPercentage ?? 0}%`}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BarChart;
